import { Button, CircularProgress } from '@shared/components';
import { EditableUnit } from '@shared/components/json-defined-form/types';
import { useToast } from '@shared/components/toast';
import { DeepLinkLocation } from '@shared/constants/identifiers';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { ExternalResource } from '@shared/features/externalResource/ExternalResource';
import { useNotLiveCustomerSendConfirmation } from '@shared/features/not-live-customer-send-confirmation/useNotLiveCustomerSendConfirmation';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { useSubmitOrder } from '@shared/hooks/useSubmitOrder';
import { Order } from '@shared/types/order';
import { useState } from 'react';

export type SubmitOrderProps = {
  order: Order;
  updateOrder: (data: EditableUnit) => Promise<void>;
  updateIsLoading: boolean;
  beforeSubmit?: () => Promise<boolean>;
};

function SubmitOrder(props: SubmitOrderProps) {
  const { sendToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    hasCompletedAction,
    hasSubmittedInternally,
    isInternalSubmission,
    submitButtonLabel,
    submitOrder,
  } = useSubmitOrder(props.order);
  const { confirmSend, confirmationDialog } =
    useNotLiveCustomerSendConfirmation(props.order);
  const { track } = useAnalytics();
  const { me, isVoomaAdmin } = useMeContext();

  const submit = async () => {
    if (!props.order) return;
    await track('Clicked Send to TMS', { orderId: props.order.id });
    if (props.beforeSubmit) {
      const val = await props.beforeSubmit();
      if (!val) {
        sendToast('Required fields are missing', {
          variant: 'error',
          isDismissible: true,
        });
        return;
      }
    }
    if (!(await confirmSend())) return;
    setIsSubmitting(true);
    await submitOrder();
    setIsSubmitting(false);
  };

  const { order } = props;

  const hasBeenSubmitted = order.hasBeenSubmitted;
  const organizationSettings = me?.organizationsDetails[0]?.settings;
  const disableResendToTms =
    organizationSettings?.build?.disableResendToTms ?? false;
  const shouldHideResendButton =
    !isInternalSubmission &&
    hasBeenSubmitted &&
    disableResendToTms &&
    !isVoomaAdmin;

  const disable = isSubmitting || hasSubmittedInternally;

  const externalResource = (
    <ExternalResource
      externalId={order.externalId ?? null}
      externalLink={order.externalDeepLink ?? null}
      handleExternalLinkPress={() => {
        track('Deep Link Clicked', {
          orderId: order.id,
          name: DeepLinkLocation.ORDER_REVIEW_FORM,
        });
      }}
      handleExternalIdPress={() => {
        track('Copy TMS Id Clicked', {
          orderId: order?.id,
        });
      }}
    />
  );

  return (
    <div className="sticky bottom-0 flex flex-col justify-center gap-2 border-t bg-white p-4 px-14">
      {!shouldHideResendButton && (
        <Button
          isDisabled={disable}
          variant={hasCompletedAction ? 'secondary' : 'primary'}
          className="w-full pb-2"
          onPress={() => {
            void submit();
          }}
          icon={
            isSubmitting ? (
              <svg>
                <CircularProgress isInverted={!hasCompletedAction} />
              </svg>
            ) : undefined
          }
          iconPosition="right"
        >
          {submitButtonLabel}
        </Button>
      )}
      {externalResource}
      {confirmationDialog}
    </div>
  );
}

export default SubmitOrder;
